import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Payment as PaymentIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { useRecordPayment, useMpesaSTKPush } from '../../hooks/payments/usePayments';
import { PaymentRecordRequest } from '../../api/types/payment.types';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../hooks/auth/useAuth';

const Payments: React.FC = () => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [paymentMethod, setPaymentMethod] = useState<string>('MPESA');

  const recordPaymentMutation = useRecordPayment();
  const mpesaSTKPushMutation = useMpesaSTKPush();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PaymentRecordRequest>();

  const onSubmit = async (data: PaymentRecordRequest) => {
    try {
      if (data.paymentMethod === 'MPESA' && paymentMethod === 'MPESA') {
        // Initiate M-Pesa STK Push
        const stkResult = await mpesaSTKPushMutation.mutateAsync({
          loanId: data.loanId,
          phoneNumber: data.phoneNumber,
          amount: data.amount,
          accountReference: `LOAN-${data.loanId.substring(0, 8)}`,
          transactionDesc: 'Loan repayment',
        });

        if (stkResult.success) {
          enqueueSnackbar('M-Pesa payment request sent!', { variant: 'success' });
        }
      } else {
        // Record manual payment
        await recordPaymentMutation.mutateAsync({
          ...data,
          recordedBy: user?.userId || '',
        });
        enqueueSnackbar('Payment recorded successfully!', { variant: 'success' });
        reset();
      }
    } catch (error) {
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Payment failed',
        { variant: 'error' }
      );
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Record Payment
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Record loan repayments via M-Pesa, cash, or bank transfer
        </Typography>
      </Box>

      <Card sx={{ maxWidth: 600 }}>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Controller
                name="loanId"
                control={control}
                rules={{ required: 'Loan ID is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Loan ID"
                    required
                    error={!!errors.loanId}
                    helperText={errors.loanId?.message || 'Enter the loan UUID'}
                    fullWidth
                  />
                )}
              />

              <Controller
                name="amount"
                control={control}
                rules={{
                  required: 'Amount is required',
                  min: { value: 1, message: 'Amount must be positive' },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Amount (KES)"
                    type="number"
                    required
                    error={!!errors.amount}
                    helperText={errors.amount?.message}
                    fullWidth
                  />
                )}
              />

              <Controller
                name="paymentMethod"
                control={control}
                defaultValue={'MPESA' as any} // eslint-disable-line @typescript-eslint/no-explicit-any
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Payment Method"
                    select
                    required
                    fullWidth
                    onChange={(e) => {
                      field.onChange(e);
                      setPaymentMethod(e.target.value as any); // eslint-disable-line @typescript-eslint/no-explicit-any
                    }}
                  >
                    <MenuItem value="MPESA">M-Pesa</MenuItem>
                    <MenuItem value="CASH">Cash</MenuItem>
                    <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
                    <MenuItem value="CHEQUE">Cheque</MenuItem>
                  </TextField>
                )}
              />

              <Controller
                name="phoneNumber"
                control={control}
                rules={{
                  required: 'Phone number is required',
                  pattern: {
                    value: /^254[17]\d{8}$/,
                    message: 'Must start with 254 and be 12 digits',
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Phone Number"
                    required
                    placeholder="254712345678"
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber?.message}
                    fullWidth
                  />
                )}
              />

              <Controller
                name="paymentReference"
                control={control}
                rules={{ required: 'Reference is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Payment Reference"
                    required
                    error={!!errors.paymentReference}
                    helperText={errors.paymentReference?.message || 'Transaction ID or receipt number'}
                    fullWidth
                  />
                )}
              />

              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Notes (optional)"
                    multiline
                    rows={2}
                    fullWidth
                  />
                )}
              />

              {paymentMethod === 'MPESA' && (
                <Alert severity="info">
                  Click submit to send M-Pesa STK Push to customer's phone
                </Alert>
              )}

              <Button
                type="submit"
                variant="contained"
                size="large"
                startIcon={<PaymentIcon />}
                disabled={recordPaymentMutation.isPending || mpesaSTKPushMutation.isPending}
                sx={{ mt: 2 }}
              >
                {recordPaymentMutation.isPending || mpesaSTKPushMutation.isPending ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Processing...
                  </>
                ) : paymentMethod === 'MPESA' ? (
                  'Send M-Pesa Request'
                ) : (
                  'Record Payment'
                )}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Payments;
